// src/App.js
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';

import ProtectedRoute from './components/ProtectedRoute';
import SuperAdminProtectedRoute from './superadmin/components/SuperAdminProtectedRoute';

// Pages
import WelcomePage from './features/welcome/pages/WelcomePage';
import LoginPage from './features/auth/pages/LoginPage';
import RegisterPage from './features/auth/pages/RegisterPage';
import ForgotPasswordPage from './features/auth/pages/ForgotPasswordPage';
import ResetPasswordPage from './features/auth/pages/ResetPasswordPage';


// Dashboard page components
import Dashboard from './features/dashboard/Dashboard';
import ReportTypesPage from './features/dashboard/pages/ReportTypesPage';
import MyActivitiesPage from './features/dashboard/pages/MyActivitiesPage';
import ReportActivitiesPage from './features/dashboard/pages/ReportActivitiesPage';
import ProfilePage from './features/dashboard/pages/ProfilePage';

// Reports pages
import ExpenseReportPage from './features/reports/pages/expense/ExpenseReportPage';
import AddExpensePage from './features/reports/pages/expense/AddExpensePage';
import EditExpensePage from './features/reports/pages/expense/EditExpensePage';
import ExpenseDetailPage from './features/reports/pages/expense/ExpenseDetailPage';
import ExpenseCategoryPage from './features/reports/pages/expense/ExpenseCategoryPage';

import RevenueReportPage from './features/reports/pages/revenue/RevenueReportPage';
import AddRevenuePage from './features/reports/pages/revenue/AddRevenuePage';
import EditRevenuePage from './features/reports/pages/revenue/EditRevenuePage';
import RevenueDetailPage from './features/reports/pages/revenue/RevenueDetailPage';
import RevenueCategoryPage from './features/reports/pages/revenue/RevenueCategoryPage';

import BillsPage from './features/reports/pages/bills/BillsPage';
import AddBillPage from './features/reports/pages/bills/AddBillPage';
import EditBillPage from './features/reports/pages/bills/EditBillPage';
import BillDetailPage from './features/reports/pages/bills/BillDetailPage';

import ProformasPage from './features/reports/pages/proforma/ProformasPage';
import AddProformaPage from './features/reports/pages/proforma/AddProformaPage';
import EditProformaPage from './features/reports/pages/proforma/EditProformaPage';
import ProformaDetailPage from './features/reports/pages/proforma/ProformaDetailPage';

// Collaborators pages
import CollaboratorsPage from './features/collaborators/pages/CollaboratorsPage';
import InvitationsPage from './features/collaborators/pages/InvitationsPage';
import ReceivedInvitationsPage from './features/collaborators/pages/RecievedInvitations';
import InviterExpensesPage from './features/collaborators/pages/expenses/InviterExpensePage';
import AddInviterExpensePage from './features/collaborators/pages/expenses/AddInviterExpensePage';
import EditInviterExpensePage from './features/collaborators/pages/expenses/EditInviterExpensePage';
import InviterExpenseDetailPage from './features/collaborators/pages/expenses/InviterExpenseDetailPage';
import InviterExpenseCategoriesPage from './features/collaborators/pages/expenses/InviterExpenseCategoriesPage';
import InviterRevenuesPage from './features/collaborators/pages/revenues/InviterRevenuesPage';
import AddInviterRevenuePage from './features/collaborators/pages/revenues/AddInviterRevenuePage';
import EditInviterRevenuePage from './features/collaborators/pages/revenues/EditInviterRevenuePage';
import InviterRevenueDetailPage from './features/collaborators/pages/revenues/InviterRevenueDetailPage';
import InviterRevenueCategoriesPage from './features/collaborators/pages/revenues/InviterRevenueCategoriesPage';
import InviterBillsPage from './features/collaborators/pages/Bills/InviterBillsPage';
import AddInviterBillPage from './features/collaborators/pages/Bills/AddInviterBillPage';
import EditInviterBillPage from './features/collaborators/pages/Bills/EditInviterBillPage';
import InviterBillDetailPage from './features/collaborators/pages/Bills/InviterBillDetailPage';
import InviterProformasPage from './features/collaborators/pages/proformas/InviterProformasPage';
import AddInviterProformaPage from './features/collaborators/pages/proformas/AddInviterProformaPage';
import EditInviterProformaPage from './features/collaborators/pages/proformas/EditInviterProformaPage';
import InviterProformaDetailPage from './features/collaborators/pages/proformas/InviterProformaDetailPage';
import SuperAdminLoginPage from './superadmin/pages/LoginPage';
import SuperAdminDashboard from './superadmin/pages/dashboard/DashboardPage';
import ManageUsersPage from './superadmin/pages/dashboard/users/ManageUsersPage';
import UserDetailsPage from './superadmin/pages/dashboard/users/UserDetailsPage';
import ManageExpensesPage from './superadmin/pages/dashboard/expenses/ManageExpensesPage';
import ExpenseDetailsPage from './superadmin/pages/dashboard/expenses/ExpenseDetailsPage';
import ManageRevenuesPage from './superadmin/pages/dashboard/revenues/ManageRevenuesPage';
import RevenueDetailsPage from './superadmin/pages/dashboard/revenues/RevenueDetailsPage';
import ManageBillsPage from './superadmin/pages/dashboard/bills/ManageBillsPage';
import BillDetailsPage from './superadmin/pages/dashboard/bills/BillDetailsPage';
import ManageProformasPage from './superadmin/pages/dashboard/proformas/ManageProformasPage';
import ProformaDetailsPage from './superadmin/pages/dashboard/proformas/ProformaDetailsPage';
import SettingsPage from './superadmin/pages/dashboard/SettingsPage';

// Styles
import './styles/theme.css';
import './styles/utilities.css';
import './styles/base.css';
import SentInvitationsPage from './features/collaborators/pages/SentInvitationsPage';
import DashboardHome from './features/dashboard/pages/DashboardHome';

function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<WelcomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:uidb64/:token" element={<ResetPasswordPage />} />
          
          {/* Superadmin login */}
          <Route path="/user-0-login/super-access/admin-user" element={<SuperAdminLoginPage />} />
          
          {/* Superadmin dashboard */}
          <Route
            path="/superadmin/dashboard"
            element={
              <SuperAdminProtectedRoute>
                <SuperAdminDashboard />
              </SuperAdminProtectedRoute>
            }
          />
          
          {/* Superadmin users management */}
          <Route
            path="/superadmin/users"
            element={
              <SuperAdminProtectedRoute>
                <ManageUsersPage />
              </SuperAdminProtectedRoute>
            }
          />
          
          {/* Superadmin user details */}
          <Route
            path="/superadmin/users/:userId"
            element={
              <SuperAdminProtectedRoute>
                <UserDetailsPage />
              </SuperAdminProtectedRoute>
            }
          />

          {/* Superadmin expenses management */}
          <Route
            path="/superadmin/expenses"
            element={
              <SuperAdminProtectedRoute>
                <ManageExpensesPage />
              </SuperAdminProtectedRoute>
            }
          />
          
          {/* Superadmin expense details */}
          <Route
            path="/superadmin/expenses/:expenseId"
            element={
              <SuperAdminProtectedRoute>
                <ExpenseDetailsPage />
              </SuperAdminProtectedRoute>
            }
          />

          {/* Superadmin revenues management */}
          <Route
            path="/superadmin/revenues"
            element={
              <SuperAdminProtectedRoute>
                <ManageRevenuesPage />
              </SuperAdminProtectedRoute>
            }
          />
          
          {/* Superadmin revenue details */}
          <Route
            path="/superadmin/revenues/:revenueId"
            element={
              <SuperAdminProtectedRoute>
                <RevenueDetailsPage />
              </SuperAdminProtectedRoute>
            }
          />

          {/* Superadmin bills management */}
          <Route
            path="/superadmin/bills"
            element={
              <SuperAdminProtectedRoute>
                <ManageBillsPage />
              </SuperAdminProtectedRoute>
            }
          />
          
          {/* Superadmin bill details */}
          <Route
            path="/superadmin/bills/:billId"
            element={
              <SuperAdminProtectedRoute>
                <BillDetailsPage />
              </SuperAdminProtectedRoute>
            }
          />

          {/* Superadmin proformas management */}
          <Route
            path="/superadmin/proformas"
            element={
              <SuperAdminProtectedRoute>
                <ManageProformasPage />
              </SuperAdminProtectedRoute>
            }
          />
          
          {/* Superadmin proforma details */}
          <Route
            path="/superadmin/proformas/:proformaId"
            element={
              <SuperAdminProtectedRoute>
                <ProformaDetailsPage />
              </SuperAdminProtectedRoute>
            }
          />

          {/* Superadmin settings */}
          <Route
            path="/superadmin/settings"
            element={
              <SuperAdminProtectedRoute>
                <SettingsPage />
              </SuperAdminProtectedRoute>
            }
          />

          {/* Dashboard Routes */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          >
            {/* Nested Routes inside Dashboard */}
            <Route index element={<DashboardHome />} />
            <Route path="profile-page" element={<ProfilePage />} />
            <Route path="report-types" element={<ReportTypesPage />} />

            {/* Expense Routes */}
            <Route path="expense-reports" element={<ExpenseReportPage />} />
            <Route path="expense-reports/new" element={<AddExpensePage />} />
            <Route path="expense-reports/:id" element={<ExpenseDetailPage />} />
            <Route path="expense-reports/:id/edit" element={<EditExpensePage />} />
            <Route path="expense-categories" element={<ExpenseCategoryPage />} />
            
            {/* Revenue Routes */}
            <Route path="revenue-reports" element={<RevenueReportPage />} />
            <Route path="revenue-categories" element={<RevenueCategoryPage />} />
            <Route path="revenue-reports/new" element={<AddRevenuePage />} />
            <Route path="revenue-reports/:id" element={<RevenueDetailPage />} />
            <Route path="revenue-reports/:id/edit" element={<EditRevenuePage />} />

            {/* Bill Routes */}
            <Route path="bills" element={<BillsPage />} />
            <Route path="bills/new" element={<AddBillPage />} />
            <Route path="bills/:id" element={<BillDetailPage />} />
            <Route path="bills/:id/edit" element={<EditBillPage />} />

            {/* Proforma Routes */}
            <Route path="proformas" element={<ProformasPage />} />
            <Route path="proformas/add" element={<AddProformaPage />} />
            <Route path="proformas/edit/:id" element={<EditProformaPage />} />
            <Route path="proformas/:id" element={<ProformaDetailPage />} />

            {/* Collaborator Routes */}
            <Route path="invitations/" element={<InvitationsPage />} />
            <Route path="invitations/sent" element={<SentInvitationsPage />} />
            <Route path="invitations/received" element={<ReceivedInvitationsPage />} />
            <Route path="collaborator/:invitationId" element={<CollaboratorsPage />} />
            {/* <Route path="collaborators/inviter-reports/:invitationId" element={<InviterReportsSummaryPage />} /> */}
            
            {/* Collaborator Expenses Routes */}
            <Route path="collaborator/:invitationId/expenses" element={<InviterExpensesPage />} />
            <Route path="collaborator/:invitationId/expenses/add" element={<AddInviterExpensePage />} />
            <Route path="collaborator/:invitationId/expenses/:id/edit" element={<EditInviterExpensePage />} />
            <Route path="collaborator/:invitationId/expenses/:id/details" element={<InviterExpenseDetailPage />} />
            <Route path="collaborator/:invitationId/expenses/categories" element={<InviterExpenseCategoriesPage />} />
            
            {/* Collaborator Revenues Routes */}
            <Route path="collaborator/:invitationId/revenues" element={<InviterRevenuesPage />} />
            <Route path="collaborator/:invitationId/revenues/add" element={<AddInviterRevenuePage />} />
            <Route path="collaborator/:invitationId/revenues/:id/edit" element={<EditInviterRevenuePage />} />
            <Route path="collaborator/:invitationId/revenues/:id/details" element={<InviterRevenueDetailPage />} />
            <Route path="collaborator/:invitationId/revenues/categories" element={<InviterRevenueCategoriesPage />} />
            
            {/* Collaborator Bills Routes */}
            <Route path="collaborator/:invitationId/bills" element={<InviterBillsPage />} />
            <Route path="collaborator/:invitationId/bills/add" element={<AddInviterBillPage />} />
            <Route path="collaborator/:invitationId/bills/:id/edit" element={<EditInviterBillPage />} />
            <Route path="collaborator/:invitationId/bills/:id/details" element={<InviterBillDetailPage />} />
            
            {/* Collaborator Proformas Routes */}
            <Route path="collaborator/:invitationId/proformas" element={<InviterProformasPage />} />
            <Route path="collaborator/:invitationId/proformas/add" element={<AddInviterProformaPage />} />
            <Route path="collaborator/:invitationId/proformas/:id/edit" element={<EditInviterProformaPage />} />
            <Route path="collaborator/:invitationId/proformas/:id/details" element={<InviterProformaDetailPage />} />

            {/* Activity Routes */}
            <Route path="my-activities" element={<MyActivitiesPage />} />
            <Route path="report-activities" element={<ReportActivitiesPage />} />
            
            {/* Profile Routes */}
            <Route path="profile" element={<ProfilePage />} />
          </Route>



          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ToastProvider>
    </AuthProvider>
  );
}

export default App;
